import type { z } from 'zod';
import { AppError } from './errors';

/** Procedure metadata — the single source for client, server and tooling (SDD §13.1).
 *  Names follow `<domain>.<module>.<verb>`; `kind` mirrors tRPC query/mutation so Phase 2 can bind the transport mechanically. */
export type ProcedureKind = 'query' | 'mutation';
export type ProcedureName = `${string}.${string}.${string}`;

export interface ProcedureDef<I extends z.ZodType = z.ZodType, O extends z.ZodType = z.ZodType> {
  readonly kind: ProcedureKind;
  readonly input: I;
  readonly output: O;
  /** Human summary for docs/tooling. */
  readonly summary: string;
}
export type ProcedureMap = Readonly<Record<string, ProcedureDef>>;
/** Compile-time guard: every key must be `<domain>.<module>.<verb>`. */
type NamesValid<P> = { readonly [K in keyof P]: K extends ProcedureName ? P[K] : never };

export function query<I extends z.ZodType, O extends z.ZodType>(
  summary: string,
  input: I,
  output: O,
): ProcedureDef<I, O> {
  return { kind: 'query', input, output, summary };
}
export function mutation<I extends z.ZodType, O extends z.ZodType>(
  summary: string,
  input: I,
  output: O,
): ProcedureDef<I, O> {
  return { kind: 'mutation', input, output, summary };
}
/** Identity helper that keeps literal keys (`const` generic) so the router shape can be derived from the names. */
export function defineProcedures<const P extends ProcedureMap>(procedures: P & NamesValid<P>): P {
  return procedures;
}

/** A callable procedure: `(input) => Promise<Output>`. Procedures whose input accepts `undefined` (void / optional
 *  object) are callable with no argument. */
export type ProcedureFn<D extends ProcedureDef> =
  D extends ProcedureDef<infer I, infer O>
    ? undefined extends z.input<I>
      ? (input?: z.input<I>) => Promise<z.output<O>>
      : (input: z.input<I>) => Promise<z.output<O>>
    : never;

type Split<K extends string> = K extends `${infer D}.${infer M}.${infer V}` ? [D, M, V] : never;
type DomainsOf<P extends ProcedureMap> = Split<keyof P & string>[0];
type ModulesOf<P extends ProcedureMap, D extends string> = Extract<
  Split<keyof P & string>,
  [D, string, string]
>[1];
type VerbsOf<P extends ProcedureMap, D extends string, M extends string> = Extract<
  Split<keyof P & string>,
  [D, M, string]
>[2];

/** The nested client shape derived from a procedure map: `client.<domain>.<module>.<verb>(input)` —
 *  the same path a tRPC router exposes, so Phase 2 swaps the transport behind the same calls. */
export type RouterShape<P extends ProcedureMap> = {
  readonly [D in DomainsOf<P>]: {
    readonly [M in ModulesOf<P, D>]: {
      readonly [V in VerbsOf<P, D, M>]: `${D}.${M}.${V}` extends keyof P
        ? ProcedureFn<P[`${D}.${M}.${V}`]>
        : never;
    };
  };
};

/** Handler map for an in-process binding: one function per procedure name, typed from the schemas. */
export type ProcedureHandlers<P extends ProcedureMap> = {
  readonly [K in keyof P]: (
    input: z.output<P[K]['input']>,
  ) => z.input<P[K]['output']> | Promise<z.input<P[K]['output']>>;
};

/** Binds handlers to a procedure map in-process: every call validates its input (VALIDATION on failure) and its output
 *  (INTERNAL on failure — a binding bug, never user data) and returns the `RouterShape` the client consumes.
 *  Phase 1 sim link and any future in-process binding share this one wrapper. */
export function bindProcedures<P extends ProcedureMap>(
  procedures: P,
  handlers: ProcedureHandlers<P>,
  opts: { onCall?: (name: keyof P & string) => void } = {},
): RouterShape<P> {
  const root: Record<string, Record<string, Record<string, (input: unknown) => Promise<unknown>>>> = {};
  for (const name of Object.keys(procedures) as (keyof P & string)[]) {
    const def: ProcedureDef | undefined = procedures[name];
    const handler = handlers[name];
    const [domain, mod, verb] = name.split('.');
    if (!def || !domain || !mod || !verb) throw new Error(`bad procedure name ${name}`);
    const modules = (root[domain] ??= {});
    const verbs = (modules[mod] ??= {});
    verbs[verb] = async (rawInput: unknown) => {
      opts.onCall?.(name);
      const parsedInput = def.input.safeParse(rawInput);
      if (!parsedInput.success) {
        throw new AppError({
          code: 'VALIDATION',
          message: `Invalid input for ${name}`,
          issues: parsedInput.error.issues.map((i) => ({
            path: i.path.map(String).join('.'),
            message: i.message,
          })),
        });
      }
      const result = await handler(parsedInput.data as z.output<P[typeof name]['input']>);
      const parsedOutput = def.output.safeParse(result);
      if (!parsedOutput.success) {
        throw new AppError({
          code: 'INTERNAL',
          message: `Output of ${name} failed its contract: ${parsedOutput.error.issues
            .slice(0, 3)
            .map((i) => `${i.path.map(String).join('.')}: ${i.message}`)
            .join('; ')}`,
          requestId: `bind:${name}`,
        });
      }
      return parsedOutput.data;
    };
  }
  return root as unknown as RouterShape<P>;
}
